import { useMemo, useState } from "react";

/**
 * Reusable sort hook for table listings.
 * @param {Array} rows - the data array
 * @param {string} defaultKey - initial sort key (e.g., "name")
 * @param {"asc"|"desc"} defaultDir - initial direction (default "asc")
 * @returns {{ sortedRows, sortKey, sortDir, toggleSort }}
 */
export function useTableSort(rows, defaultKey, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const key = sortKey;
    if (!key) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = pluck(a, key);
      const bv = pluck(b, key);
      return compare(av, bv);
    });
    if (sortDir === "desc") arr.reverse();
    return arr;
  }, [rows, sortKey, sortDir]);

  return { sortedRows, sortKey, sortDir, toggleSort };
}

// Support "a.b.c" style keys and function accessors
function pluck(obj, key) {
  if (typeof key === "function") return key(obj);
  if (obj == null) return undefined;
  return key.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}

function compare(a, b) {
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;   // empty always at bottom
  if (bEmpty) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a === b) ? 0 : (a ? -1 : 1);
  // Dates as ISO strings sort correctly with string compare, but coerce numbers-as-strings to number
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB) && typeof a !== "string") return numA - numB;
  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
}
